<template>
    <modal
      name="EditCourseSectionModal"
      :width="750"
      height="auto"
      :scrollable="true"
      @before-open="beforeOpen"
    >
      <div class="modal-content">
        <div class="modal-header">
          <div class="modal-title" id="exampleModalLabel">
            <h5>Transfer Student to Another Course</h5>
          </div>
          <button type="button" class="close" @click="hide('EditCourseSectionModal')">
            <em class="icon-close"></em>
          </button>
        </div>
        <div class="modal-body mt-3">
            <div class="edit-section-info">
                <p class="name">User name: <strong>{{user_info.first_name}} {{user_info.last_name}}</strong></p>
                <p class="current_course">Current course: <strong>{{user_info.course_name}}</strong></p>
                <p class="current_section">Current section: <strong>{{user_info.section_name}}</strong></p>

                <div class="transfer-info-banner">
                  <p>
                    Only courses with the same Learning Objectives as the student's current course will appear in search results.
                    Use this to move a student between cohorts (e.g., Fall 2024 → Fall 2025) — their progress will be preserved.
                  </p>
                </div>

                <label class="form-label" for="course-search">Search Course:</label>
                <input
                  id="course-search"
                  type="text"
                  v-model="search_query"
                  @input="onSearchInput"
                  @blur="onSearchBlur"
                  class="form-control"
                  placeholder="Type to search courses..."
                  :disabled="isSubmitting"
                  aria-label="Search for a course"
                  autocomplete="off"
                />
                <div v-if="courseSearchResults.length" class="dropdown-menu show">
                    <ul class="dropdown-list">
                        <li class="dropdown-items" v-for="course in courseSearchResults" :key="course.id">
                            <div class="dropdown-content">
                                <h4 :title="course.name || (course.attributes && course.attributes.name)">{{ course.name || (course.attributes && course.attributes.name) }}</h4>
                            </div>
                            <div class="dropdown-action">
                                <a @click="selectCourse(course)" class="add-action">Select</a>
                            </div>
                        </li>
                    </ul>
                </div>
                <!-- Search loading state -->
                <div v-if="isLoadingCourses" class="search-status">Searching...</div>

                <!-- No results state -->
                <div v-if="hasSearched && !isLoadingCourses && !courseSearchResults.length && !selected_course" class="search-status no-results">
                    No matching courses found.
                </div>

                <div v-if="selected_course_name && userHasSelectedCourse" class="selected-course-display">
                    <span>{{ selected_course_name }}</span>
                    <a @click="clearCourseSelection" class="remove-link">Clear</a>
                </div>
                
                <label class="form-label mt-3" for="section-select">Select Section:</label>
                <select
                  id="section-select"
                  v-model="selected_section"
                  class="form-select"
                  :disabled="!selected_course || isLoadingSections || isSubmitting"
                  aria-label="Select a section"
                  aria-required="true"
                >
                    <option disabled value="">Please select section:</option>
                    <option v-for="section in all_sections" v-bind:value="section[1]" :key="section[1]">
                        {{section[0]}}
                    </option>
                </select>
            </div>
            <div v-if="errorMessage" class="alert alert-danger" role="alert" aria-live="polite">
              {{ errorMessage }}
            </div>
            <div class="edit-section-info__buttons">
                <button class="btn secondary" @click="changeCourseSection" :disabled="!selected_section || isSubmitting || !userHasSelectedCourse">Save Changes</button>
            </div>
        </div>
      </div>
    </modal>
</template>

<script>
import api from "../../services/api";
// import { Form as VeeForm, Field as VeeField, ErrorMessage as VeeErrorMessage } from "vee-validate";
import { utilFunctionService } from "@/utils/utils.service";
import { commonConfig } from "@/utils/commonConfig";
export default {
  name: "EditCourseSectionModal",
  components: {
  },
  data() {
    return {
        user_info: {},
        user_id: "",
        section_id: "",
        course_id: "",
        courseSearchResults: [],
        all_sections: [],
        selected_course: "",
        selected_course_name: "",
        selected_section: "",
        search_query: "",
        searchTimeout: null,
        hasSearched: false,
        isLoadingCourses: false,
        isLoadingSections: false,
        isSubmitting: false,
        errorMessage: "",
        userHasSelectedCourse: false
    };
  },
  methods: {
    beforeOpen(event) {
        this.user_info = event.params.user_info;
        this.user_id = event.params.user_info.user_id;
        this.section_id = event.params.user_info.section_id;
        this.course_id = event.params.user_info.course_id;

        // Default selected_course/section to current values so the form is immediately usable
        this.selected_course = String(this.course_id);
        this.selected_course_name = event.params.user_info.course_name || "";
        this.selected_section = event.params.user_info.section_id;
        this.courseSearchResults = [];
        this.all_sections = [];
        this.search_query = "";
        this.hasSearched = false;
        this.errorMessage = "";
        this.userHasSelectedCourse = true;

        this.loadOriginalSections();
    },
    loadOriginalSections() {
        this.isLoadingSections = true;
        utilFunctionService.showLoader();
        api.getEditSectionDetails(this.section_id)
          .then(res => {
            this.all_sections = res.data?.all_sections || [];
            this.isLoadingSections = false;
            utilFunctionService.hideLoader();
          })
          .catch(() => {
            this.errorMessage = "Failed to load sections. Please try again.";
            this.isLoadingSections = false;
            utilFunctionService.hideLoader();
            utilFunctionService.showErr("Unable to load sections");
          });
    },
    hide(modal) {
      this.$modal.hide(modal);
    },
    onSearchInput() {
        clearTimeout(this.searchTimeout);
        this.selected_course = "";
        this.selected_course_name = "";
        this.selected_section = "";
        this.userHasSelectedCourse = false;

        if (this.search_query.length < 2) {
            this.courseSearchResults = [];
            this.hasSearched = false;
            this.isLoadingCourses = false;
            return;
        }

        this.searchTimeout = setTimeout(() => {
            if (!this.course_id) {
                this.courseSearchResults = [];
                this.hasSearched = true;
                this.isLoadingCourses = false;
                utilFunctionService.showErr("Unable to search: current course not identified. Please refresh and try again.");
                return;
            }
            this.all_sections = [];
            this.isLoadingCourses = true;
            api.searchCourses(this.search_query, this.course_id)
              .then(res => {
                this.courseSearchResults = res.data?.data?.courses || [];
                this.hasSearched = true;
                this.isLoadingCourses = false;
              })
              .catch(() => {
                this.courseSearchResults = [];
                this.hasSearched = true;
                this.isLoadingCourses = false;
                utilFunctionService.showErr("Unable to search courses");
              });
        }, 300);
    },
    selectCourse(course) {
        this.selected_course = String(course.id);
        this.selected_course_name = course.name || course.attributes?.name || "Unknown Course";
        this.search_query = "";
        this.courseSearchResults = [];
        this.userHasSelectedCourse = true;
        this.onCourseChange();
    },
    clearCourseSelection() {
        this.selected_course = String(this.course_id);
        this.selected_course_name = this.user_info.course_name || "";
        this.selected_section = "";
        this.userHasSelectedCourse = true;
        this.search_query = "";
        this.courseSearchResults = [];
        this.loadOriginalSections();
    },
    onCourseChange() {
        this.selected_section = "";
        this.all_sections = [];
        this.errorMessage = "";

        if (this.selected_course) {
            this.isLoadingSections = true;
            utilFunctionService.showLoader();
            api.getSectionsByCourse(this.selected_course, this.section_id, this.user_id)
              .then(res => {
                this.all_sections = res.data.data?.sections || res.data.sections || [];
                this.isLoadingSections = false;
                utilFunctionService.hideLoader();
                if (this.all_sections.length === 0) {
                    utilFunctionService.showErr("No compatible sections found for this course");
                    this.selected_course = "";
                    this.selected_course_name = "";
                    this.userHasSelectedCourse = false;
                }
              })
              .catch(() => {
                this.isLoadingSections = false;
                utilFunctionService.hideLoader();
                utilFunctionService.showErr("Error loading sections for selected course");
              });
        }
    },
    onSearchBlur() {
        setTimeout(() => {
            this.courseSearchResults = [];
        }, 150);
    },
    beforeUnmount() {
        clearTimeout(this.searchTimeout);
    },
    changeCourseSection() {
        if (this.isSubmitting) return;
        this.errorMessage = "";

        if (!confirm(`Are you sure you want to transfer ${this.user_info.first_name} ${this.user_info.last_name} to ${this.selected_course_name}? Their progress will be preserved.`)) {
            return;
        }

        const newCourseId = (this.selected_course && this.selected_course !== String(this.course_id))
            ? this.selected_course
            : null;

        this.isSubmitting = true;
        utilFunctionService.showLoader();
        api.updateEditSection(this.user_id, this.section_id, this.selected_section, newCourseId)
        .then(res => {
            this.isSubmitting = false;
            utilFunctionService.hideLoader();
            if (res.data.status === "ok") {
                this.hide('EditCourseSectionModal')
                utilFunctionService.showSuccess(
                    commonConfig.appMessages.sectionChangeSuccess
                );
                setTimeout(() => {
                    this.$parent.refreshEditSection();
                }, 3000)
            }
        })
        .catch(err => {
            this.isSubmitting = false;
            utilFunctionService.hideLoader();
            const message = err.response?.data?.error || commonConfig.appMessages.sectionChangeError;
            utilFunctionService.showErr(message);
        })
    }
  }
};
</script>
<style lang="scss">
@import "../../styles/variables.scss";
@import "../../styles/mixin.scss";

.edit-section-info {
    margin-bottom: 32px;
    .name {
        text-transform: capitalize;
        font-size: 18px;
        margin-bottom: 12px;
        font-weight: 500;
    }
    .current_course {
        font-size: 16px;
        margin-bottom: 8px;
    }
    .current_section {
        font-size: 16px;
        margin-bottom: 20px;
    }
    .transfer-info-banner {
        background-color: #eaf6f2;
        border-left: 4px solid #3dbc9e;
        border-radius: 4px;
        padding: 10px 14px;
        margin-bottom: 20px;
        p {
            margin: 0;
            font-size: 13px;
            color: #2d6a5a;
            line-height: 1.5;
        }
    }
    .form-label {
        display: block;
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 8px;
        margin-top: 16px;
    }
    .form-control {
        width: 100%;
        padding: 10px;
        font-size: 14px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: #fff;
        &:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
            opacity: 0.6;
        }
    }
    .form-select {
        width: 100%;
        padding: 10px;
        font-size: 14px;
        border: 1px solid #ddd;
        border-radius: 4px;
        background-color: #fff;
        &:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
            opacity: 0.6;
        }
    }
    .dropdown-menu {
        position: relative;
        display: block;
        max-height: 200px;
        overflow-y: auto;
        overflow-x: hidden;
        border: 1px solid #ddd;
        border-radius: 4px;
        margin-top: 4px;
        padding: 0;
    }
    .dropdown-list {
        margin: 0;
        padding: 0;
        list-style: none;
    }
    .dropdown-items {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 20px 10px 15px;
        border-bottom: 1px solid #f3f5f9;
        &:last-child {
            border-bottom: none;
        }
        &:hover {
            background-color: #f9f9f9;
        }
    }
    .dropdown-content {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        padding-right: 10px;
        h4 {
            margin: 0;
            font-size: 14px;
            font-weight: 500;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }
    .dropdown-action {
        flex-shrink: 0;
        margin-left: 10px;
        .add-action {
            display: inline-block;
            width: 60px;
            height: 100%;
            padding: 6px 12px;
            font-size: 13px;
            font-weight: 500;
            color: #fff;
            background-color: #3dbc9e;
            border-radius: 4px;
            cursor: pointer;
            text-decoration: none;
            white-space: nowrap;
            &:hover {
                background-color: #35a88c;
            }
        }
    }
    .search-status {
        font-size: 13px;
        color: #666;
        padding: 8px 0;
        &.no-results {
            color: #888;
            font-style: italic;
        }
    }
    .selected-course-display {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px;
        background: #f5f5f5;
        border-radius: 4px;
        margin-top: 8px;
        .remove-link {
            color: #dc3545;
            cursor: pointer;
            text-decoration: underline;
        }
    }
}
.alert {
    padding: 10px 14px;
    border-radius: 4px;
    margin-bottom: 12px;
    font-size: 14px;
}
.alert-danger {
    background-color: #fdecea;
    border: 1px solid #f5c6cb;
    color: #842029;
}
.edit-section-info__buttons {
    margin-top: 24px;
    .secondary {
        color: #000000;
        padding: 10px 20px;
        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
    .secondary:hover:not(:disabled) {
        color: #ffffff;
        background: #3dbc9e;
    }
}
</style>
